import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { UsersByKeywordQueryVariables, useUsersByKeywordQuery } from "../../graphql"
import { UsersList } from "./UsersList"

export type UsersByKeywordProps = {
	keyword: string
	typeId?: string
}

export const UsersByKeyword: React.FC<UsersByKeywordProps> = ({ keyword, typeId }) => {
	const [filter, setFilter] = useState({ typeId, keyword })

	const [pagination, setPagination] = useState<UsersByKeywordQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = useUsersByKeywordQuery({
		variables: { ...filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ keyword, typeId })
		}, 1000)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword, typeId])

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.usersByKeyword.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.usersByKeyword ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading users</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.usersByKeyword.users && data.usersByKeyword.users.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.usersByKeyword.users.length || 0} user
						{(data.usersByKeyword.users.length || 0) > 1 ? "s" : ""}
					</Text>

					<UsersList users={data.usersByKeyword.users} loadMore={nextPage} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more users</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any users.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
